import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Phone, Star, AlertCircle, Heart } from 'lucide-react';
import { weddingGradients } from '../styles/weddingTheme';
import { providers } from '../services/api';
import useShortlist from '../hooks/useShortlist';

function ModernProviderCard({ provider }) {
  const navigate = useNavigate();
  const [publishing, setPublishing] = useState(false);
  const [imgError, setImgError] = useState(false);
  const { isShortlisted, toggle } = useShortlist();

  // Check if current user is the owner
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isOwner = currentUser.id === provider.id;

  const getGradientForProvider = (providerId) => {
    const hash = providerId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return weddingGradients[hash % weddingGradients.length];
  };

  const handleCardClick = () => {
    navigate(`/provider/${provider.id}`);
  };

  const handlePublish = async (e) => {
    e.stopPropagation();
    if (!window.confirm('Ready to publish your profile? It will be visible to everyone browsing the directory.')) {
      return;
    }

    try {
      setPublishing(true);
      await providers.publishProfile();
      window.location.reload();
    } catch (error) {
      console.error('Failed to publish profile:', error);
      alert('Failed to publish profile. Please try again.');
      setPublishing(false);
    }
  };

  const renderRating = () => {
    const rating = provider.google_rating || provider.facebook_rating;
    const reviewCount = provider.google_review_count || provider.facebook_review_count || 0;

    if (!rating) return null;

    return (
      <div className="flex items-center gap-1 text-sm">
        <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
        <span className="font-semibold">{rating.toFixed(1)}</span>
        {reviewCount > 0 && (
          <span className="text-gray-500">({reviewCount})</span>
        )}
      </div>
    );
  };

  return (
    <div
      onClick={handleCardClick}
      className="group relative bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1"
      style={{
        minHeight: '320px',
        border: isOwner && !provider.is_published ? '4px solid #dc2626' : 'none'
      }}
    >
      {/* Unpublished Warning Banner - Only for owner */}
      {isOwner && !provider.is_published && (
        <div className="absolute top-0 left-0 right-0 z-20 bg-red-600 text-white px-3 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-bold">
            <AlertCircle className="w-4 h-4" />
            UNPUBLISHED - Not visible
          </div>
          <button
            onClick={handlePublish}
            disabled={publishing}
            className="bg-white text-red-600 px-4 py-1 rounded-lg font-bold text-sm hover:bg-red-50 transition-colors disabled:opacity-50"
          >
            {publishing ? 'Publishing...' : 'Publish Now'}
          </button>
        </div>
      )}

      {/* Shortlist Heart Button */}
      <button
        onClick={(e) => { e.stopPropagation(); toggle(provider.id); }}
        className="absolute top-3 right-3 z-20 p-2 rounded-full bg-white/80 backdrop-blur-sm hover:bg-white transition-colors shadow-md"
        title={isShortlisted(provider.id) ? 'Remove from shortlist' : 'Add to shortlist'}
      >
        <Heart
          className={`w-5 h-5 transition-colors ${isShortlisted(provider.id) ? 'fill-pink-500 text-pink-500' : 'text-gray-400'}`}
        />
      </button>

      {/* Banner Image Section */}
      <div className="relative h-48 overflow-hidden">
        {(provider.banner_url || provider.thumbnail_url) && !imgError ? (
          <div className="absolute inset-0 overflow-hidden">
            <img
              src={(provider.banner_url || provider.thumbnail_url).startsWith('http') ? (provider.banner_url || provider.thumbnail_url) : `https://api.bridalconnect.co.za${provider.banner_url || provider.thumbnail_url}`}
              alt={`${provider.business_name} - ${provider.category_name || 'Wedding Service Provider'} in ${provider.city || 'KwaZulu-Natal'}`}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
              onError={() => setImgError(true)}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          </div>
        ) : (
          <div
            className="absolute inset-0 transition-transform duration-300 group-hover:scale-110"
            style={{ background: getGradientForProvider(provider.id) }}
          >
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          </div>
        )}

        {/* Business Name on Banner */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <h3 className="text-xl font-bold text-white mb-1 drop-shadow-lg">
            {provider.business_name}
          </h3>
          <div className="flex flex-wrap gap-2">
            {provider.categories && provider.categories.length > 0 && provider.categories.map((cat, idx) => (
              <span key={idx} className="inline-block px-2 py-1 bg-white/20 backdrop-blur-sm rounded-md text-xs text-white font-medium">
                {cat.name}
              </span>
            ))}
            {(!provider.categories || provider.categories.length === 0) && provider.category_name && (
              <span className="inline-block px-2 py-1 bg-white/20 backdrop-blur-sm rounded-md text-xs text-white font-medium">
                {provider.category_name}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="p-4 space-y-3">
        {provider.contact_person && (
          <div className="text-sm text-gray-600">
            {provider.contact_person}
          </div>
        )}

        {(provider.city || provider.province) && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <MapPin className="w-4 h-4 text-gray-400" />
            <span>
              {[provider.city, provider.province].filter(Boolean).join(', ')}
            </span>
          </div>
        )}

        {provider.phone && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Phone className="w-4 h-4 text-gray-400" />
            <span>{provider.phone}</span>
          </div>
        )}

        {renderRating() && (
          <div className="pt-2 border-t border-gray-100">
            {renderRating()}
          </div>
        )}
      </div>
    </div>
  );
}

export default ModernProviderCard;
